/**
 * CREATE TYPE / CREATE DOMAIN structure extractor — the grammar-backed
 * replacement for the removed regex parked-type parser (VDE-2 Slice 2). Pure,
 * regex-free: every structural decision comes from the shared keyword-
 * consumption primitives (`shared.ts`), `readIdentifier` via the qualified-
 * name reader, and `findMatchingParen` — never regex (project rule VQE-D10).
 * Low-level lexical helpers live in `type.parse.ts`; this module is the clause
 * orchestration + entry points.
 *
 * CREATE TYPE / CREATE DOMAIN is seed-only: the inline `[Edit Type]` /
 * `[Edit Domain]` popover builds SQL from form values and replaces the
 * statement text. There is no structural apply path, so the arm carries the
 * pre-parsed seed (`parsed`) plus the statement range, mirroring the byte-
 * range posture of the other arms (and the Slice-1 GRANT/REVOKE arm).
 *
 * Captured shape (best-effort, engine-spanning):
 *   CREATE [OR REPLACE] TYPE [IF [NOT] EXISTS] [schema.]name
 *          [AS ENUM (…)] | [AS (…)] | [AS OBJECT (…)] | [AS RANGE (subtype=…)]
 *          | [AS TABLE OF T] | [AS VARRAY(n) OF T] | [AS TABLE (…)]
 *          | [FROM <baseType>]
 *   CREATE DOMAIN [schema.]name [AS] <baseType> [DEFAULT|NOT NULL|CHECK|…]
 *
 * Out-of-scope inner text (domain CHECK expressions, complex composite attr
 * types) is carried verbatim in the `baseType` / `typeRef` strings — never
 * over-parsed.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D4 — Type / Domain UI.
 */

import type { ParseOptions, StatementInfo } from '../../../sql-grammar/model';
import { readIdentifier } from '../../scope/identifiers';
import { scanTopLevelKeywords } from '../../structure/scanner';
import type { DdlStructure, ParsedBuffer } from '../../types';

import { skipWsCommentsLocal, tryConsumeKeyword } from './shared';
import type { ParsedParkedType } from './typeTypes';
import {
  findMatchingParen,
  parseAttributesBody,
  parseEnumValues,
  readTypeName,
  readUintAt,
} from './type.parse';

export type { ParsedParkedType } from './typeTypes';

/** Default options for the test/string entry shell — sqlite accepts all
 *  three quoted-identifier styles (`[ ]`, backticks, `" "`), so an engine-
 *  agnostic parked statement parses regardless of source dialect. */
const STRING_SHELL_OPTIONS: ParseOptions = { engine: 'sqlite' };

/** Domain trailing clauses that terminate the `baseType` span (mirrors the
 *  removed parser's `\s(?:DEFAULT|NOT\s+NULL|CONSTRAINT|CHECK)\b`). `NOT` is
 *  included so `NOT NULL` truncates; a lone `NULL` does not. */
const DOMAIN_TRAIL_KEYWORDS: ReadonlySet<string> = new Set([
  'DEFAULT',
  'NOT',
  'CONSTRAINT',
  'CHECK',
]);

/** Locate the matched-paren body span for the `(` at-or-after `from`. Returns
 *  null when there is no `(` in `[from, end)`. When the `(` has no matching
 *  `)` the body runs to `end` (faithful to the removed parser's
 *  `findMatchingClose` < 0 → slice-to-end fallback). */
function bodySpanAfter(
  source: string,
  options: ParseOptions,
  from: number,
  end: number,
): { s: number; e: number } | null {
  let open = -1;
  for (let i = from; i < end; i++) {
    if (source[i] === '(') { open = i; break; }
  }
  if (open < 0) return null;
  const close = findMatchingParen(source, options, open, end);
  return { s: open + 1, e: close > open ? close : end };
}

/** Shared pure core. Operates on a byte range; returns the seed contract or
 *  null for non-type / structurally-incomplete input. */
function parseTypeCore(
  source: string,
  start: number,
  end: number,
  options: ParseOptions,
): ParsedParkedType | null {
  let i = skipWsCommentsLocal(source, start, end, options);
  const afterCreate = tryConsumeKeyword(source, i, end, options, 'CREATE');
  if (afterCreate === i) return null;
  i = skipWsCommentsLocal(source, afterCreate, end, options);

  let orReplace = false;
  const afterOr = tryConsumeKeyword(source, i, end, options, 'OR');
  if (afterOr !== i) {
    const o = skipWsCommentsLocal(source, afterOr, end, options);
    const afterReplace = tryConsumeKeyword(source, o, end, options, 'REPLACE');
    if (afterReplace !== o) {
      orReplace = true;
      i = skipWsCommentsLocal(source, afterReplace, end, options);
    }
  }

  let headKeyword: 'TYPE' | 'DOMAIN';
  const afterType = tryConsumeKeyword(source, i, end, options, 'TYPE');
  if (afterType !== i) { headKeyword = 'TYPE'; i = afterType; }
  else {
    const afterDomain = tryConsumeKeyword(source, i, end, options, 'DOMAIN');
    if (afterDomain !== i) { headKeyword = 'DOMAIN'; i = afterDomain; }
    else return null;
  }
  i = skipWsCommentsLocal(source, i, end, options);

  // Optional IF [NOT] EXISTS prefix.
  const afterIf = tryConsumeKeyword(source, i, end, options, 'IF');
  if (afterIf !== i) {
    let j = skipWsCommentsLocal(source, afterIf, end, options);
    const afterNot = tryConsumeKeyword(source, j, end, options, 'NOT');
    if (afterNot !== j) j = skipWsCommentsLocal(source, afterNot, end, options);
    const afterExists = tryConsumeKeyword(source, j, end, options, 'EXISTS');
    if (afterExists !== j) i = skipWsCommentsLocal(source, afterExists, end, options);
  }

  const nameRead = readTypeName(source, i, end, options);
  if (!nameRead) return null;
  const name = nameRead.name;
  i = skipWsCommentsLocal(source, nameRead.end, end, options);

  if (headKeyword === 'DOMAIN') {
    // CREATE DOMAIN name [AS] <baseType> [DEFAULT … | NOT NULL | CHECK …].
    const afterAs = tryConsumeKeyword(source, i, end, options, 'AS');
    const baseStart =
      afterAs !== i ? skipWsCommentsLocal(source, afterAs, end, options) : i;
    let baseEnd = end;
    for (const kw of scanTopLevelKeywords(source, baseStart, end, options)) {
      if (DOMAIN_TRAIL_KEYWORDS.has(kw.upper)) { baseEnd = kw.start; break; }
    }
    const baseType = source.slice(baseStart, baseEnd).trim();
    // orReplace hardcoded false for DOMAIN — faithful to the removed parser.
    return { name, headKeyword, orReplace: false, kind: 'domain', baseType };
  }

  // CREATE [OR REPLACE] TYPE name AS <kind> …
  const afterAs = tryConsumeKeyword(source, i, end, options, 'AS');
  if (afterAs !== i) {
    const afterAsWs = skipWsCommentsLocal(source, afterAs, end, options);
    const kwTok = readIdentifier(source, afterAsWs, end, options);
    const kwIdent =
      kwTok && !kwTok.quoted ? { upper: kwTok.value.toUpperCase(), end: kwTok.end } : null;
    const kwUpper = kwIdent ? kwIdent.upper : null;

    if (kwUpper === 'ENUM') {
      const body = bodySpanAfter(source, options, kwIdent!.end, end);
      if (!body) return { name, headKeyword, orReplace, kind: 'enum' };
      return {
        name,
        headKeyword,
        orReplace,
        kind: 'enum',
        enumValues: parseEnumValues(source, body.s, body.e),
      };
    }
    if (kwUpper === 'RANGE') {
      const subtype = readRangeSubtype(source, kwIdent!.end, end, options);
      return { name, headKeyword, orReplace, kind: 'range', subtype };
    }
    if (kwUpper === 'OBJECT') {
      const body = bodySpanAfter(source, options, kwIdent!.end, end);
      if (!body) return { name, headKeyword, orReplace, kind: 'object' };
      return {
        name,
        headKeyword,
        orReplace,
        kind: 'object',
        attributes: parseAttributesBody(source, body.s, body.e, options),
      };
    }
    if (kwUpper === 'VARRAY') {
      const sizeRead = readUintAt(
        source,
        skipParenOpen(source, kwIdent!.end, end, options),
        end,
        options,
      );
      if (sizeRead) {
        // Past `<n> )` then `OF` then verbatim base-type remainder.
        let p = skipWsCommentsLocal(source, sizeRead.end, end, options);
        if (source[p] === ')') p = skipWsCommentsLocal(source, p + 1, end, options);
        const afterOf = tryConsumeKeyword(source, p, end, options, 'OF');
        if (afterOf !== p) {
          const baseStart = skipWsCommentsLocal(source, afterOf, end, options);
          const baseType = source.slice(baseStart, end).trim();
          if (baseType.length > 0) {
            return {
              name,
              headKeyword,
              orReplace,
              kind: 'varray',
              varraySize: sizeRead.value,
              baseType,
            };
          }
        }
      }
      return { name, headKeyword, orReplace, kind: 'varray' };
    }
    if (kwUpper === 'TABLE') {
      // `AS TABLE OF T` (oracle nested table) vs `AS TABLE (…)` (mssql TVP).
      const afterTable = skipWsCommentsLocal(source, kwIdent!.end, end, options);
      const afterOf = tryConsumeKeyword(source, afterTable, end, options, 'OF');
      if (afterOf !== afterTable) {
        const baseStart = skipWsCommentsLocal(source, afterOf, end, options);
        const baseType = source.slice(baseStart, end).trim();
        return { name, headKeyword, orReplace, kind: 'table-of', baseType };
      }
      // mssql TVP — body left uninterpreted.
      return { name, headKeyword, orReplace, kind: 'table' };
    }

    // CREATE TYPE name AS (col1 TYPE, col2 TYPE) — pgsql composite.
    const body = bodySpanAfter(source, options, afterAsWs, end);
    if (body && source.slice(afterAsWs, body.s - 1).trim().length === 0) {
      return {
        name,
        headKeyword,
        orReplace,
        kind: 'composite',
        attributes: parseAttributesBody(source, body.s, body.e, options),
      };
    }
  }

  // CREATE TYPE name FROM <baseType> — mssql alias.
  const afterFrom = tryConsumeKeyword(source, i, end, options, 'FROM');
  if (afterFrom !== i) {
    const baseStart = skipWsCommentsLocal(source, afterFrom, end, options);
    const baseType = source.slice(baseStart, end).trim();
    return { name, headKeyword, orReplace, kind: 'alias', baseType };
  }

  // No recognised body — oracle forward declaration.
  return { name, headKeyword, orReplace, kind: null };
}

/** Skip a single leading `(` (plus surrounding ws/comments) if present so the
 *  `VARRAY(n)` size sits next. Returns the post-`(` index, else `from`. */
function skipParenOpen(
  source: string,
  from: number,
  end: number,
  options: ParseOptions,
): number {
  const p = skipWsCommentsLocal(source, from, end, options);
  return source[p] === '(' ? p + 1 : p;
}

/** Read the RANGE `subtype = <ident>` value. Walks identifier tokens at any
 *  paren depth (the body lives inside `RANGE (…)`, so a depth-0 scanner would
 *  miss it) until a bare `subtype` ident is followed by `=` then a bare
 *  identifier — faithful to the removed parser's whole-rest substring search.
 *  Returns '' when no such pair is present. */
function readRangeSubtype(
  source: string,
  from: number,
  end: number,
  options: ParseOptions,
): string {
  let i = from;
  while (i < end) {
    const tok = readIdentifier(source, i, end, options);
    if (tok && tok.end > i) {
      if (!tok.quoted && tok.value.toUpperCase() === 'SUBTYPE') {
        const eq = skipWsCommentsLocal(source, tok.end, end, options);
        if (source[eq] === '=') {
          const valStart = skipWsCommentsLocal(source, eq + 1, end, options);
          const valTok = readIdentifier(source, valStart, end, options);
          if (valTok && !valTok.quoted) return valTok.value;
        }
      }
      i = tok.end;
      continue;
    }
    i++;
  }
  return '';
}

/** Buffer-entry seed helper consumed by the inline `[Edit Type]` /
 *  `[Edit Domain]` popover. Returns the parked-type seed or null on non-type
 *  / malformed input. */
export function extractParkedType(
  buffer: ParsedBuffer,
  statementInfo: StatementInfo,
): ParsedParkedType | null {
  const { source, options } = buffer.parsedDocument;
  return parseTypeCore(
    source,
    statementInfo.range.startOffset,
    statementInfo.range.endOffset,
    options,
  );
}

/** Thin string-entry shell (tests + engine-agnostic callers). */
export function extractParkedTypeFromString(
  stmtText: string,
  options: ParseOptions = STRING_SHELL_OPTIONS,
): ParsedParkedType | null {
  return parseTypeCore(stmtText, 0, stmtText.length, options);
}

/** DSE2 arm — `extractDdlStructure` dispatches CREATE TYPE / CREATE DOMAIN
 *  here. `create-domain` when the parsed head is DOMAIN, else `create-type`
 *  (including malformed-but-CREATE-TYPE → `parsed:null`; never throws). */
export function extractType(
  buffer: ParsedBuffer,
  statementInfo: StatementInfo,
): DdlStructure {
  const parsed = extractParkedType(buffer, statementInfo);
  const kind = parsed?.headKeyword === 'DOMAIN' ? 'create-domain' : 'create-type';
  return { kind, range: statementInfo.range, parsed };
}
